import { Editor } from '@ckeditor/ckeditor5-core';
import { diffLines, type Change } from 'diff';
import { html as beautifyHTML } from 'js-beautify';
import _ from 'lodash';
import moment from 'moment';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './css/revision_preview_diff.css';
import RevisionRenamePage from './RevisionPreviewDiff/RevisionRenamePage';
import RevisionRenameWindow from './RevisionPreviewDiff/RevisionRenameWindow';

type Props = {
  editor: Editor;
  sectionId: number;
  switchEditor: (value: string) => void;
};
type RevisionItem = {
  id: string;
  index: number;
  name: string;
  users: RevisionUser[];
  createdAt: moment.Moment;
  date: string;
};
type RevisionUser = {
  id: string;
  name: string;
};
type DiffLineItem = {
  id: string;
  mode: string;
  content: string;
  added: boolean;
  removed: boolean;
};

const RevisionPreviewDiff = forwardRef(({ editor, sectionId, switchEditor }: Props, ref) => {
  const { t } = useTranslation();
  const getDateText = (date: moment.Moment): string => {
    // 今日との差分を取得
    const today = moment().startOf('day');
    const diffDays = today.diff(date.startOf('day'), 'days');
    if (diffDays === 0) {
      return t('今日');
    }
    if (diffDays === 1) {
      return t('昨日');
    }
    if (diffDays <= 6) {
      return t('{{days}}日前', { days: diffDays });
    }
    if (diffDays === 7) {
      return t('1週間前');
    }

    return date.format('YYYY.MM.DD');
  };
  /* eslint-disable */

  // Revision
  const revisionsRepository = editor.plugins.get('RevisionsRepository') as any;
  const allRevisions = revisionsRepository.getRevisions().reverse();
  const revisionItems: RevisionItem[] = allRevisions.map((revision: any, index: number) => {
    let users = revision.authors.map((author: any) => {
      return {
        id: author.id,
        name: author.name,
      };
    });
    if (revision.creator) {
      users.push({
        id: revision.creator.id,
        name: revision.creator.name,
      });
    }
    const uniqueUsers = _.uniqBy(users, 'id');
    const createdAt = moment(revision.createdAt);
    return {
      id: revision.id,
      index: index,
      name: revision.name || '---',
      users: uniqueUsers,
      createdAt: createdAt,
      date: getDateText(createdAt),
    };
  });
  const groupedRevisions = _.groupBy(revisionItems, (item: RevisionItem) => item.date);

  // Diff contents
  const [diffResult, setDiffResult] = useState<Change[]>([]);
  const diffResultCount = useMemo(() => {
    const filteredDiffResult = diffResult.filter((item) => item.added || item.removed);
    return filteredDiffResult.length;
  }, [diffResult]);
  const revisionTracker = editor.plugins.get('RevisionTracker') as any;
  const getCompareRevisionContent = (html: string) => {
    const patterns = {
      text: new RegExp('</?comment-(start|end)[^>]*>', 'g'),
      image: new RegExp('data-comment-(start-before|end-after)="[^"]*"', 'g'),
    };

    for (let key in patterns) {
      const pattern: RegExp = patterns[key as keyof typeof patterns];
      html = html.replace(pattern, '');
    }
    return html;
  };
  const makeDiff = (original: RevisionItem, revised: RevisionItem) => {
    const revisedId = _.get(revised, 'id', '');
    if (!original || revisedId === 'initial') {
      // 一番最初のバージョンは、差分を取得できないので、そのまま表示
      original = revised;
    } else if (!original || !revised) {
      return;
    }
    // 2つのRevisionからコンテンツを抽出したらDiffを作成します
    Promise.all([revisionTracker.getRevisionDocumentData(original), revisionTracker.getRevisionDocumentData(revised)])
      .then((results: any) => {
        const originalContent = _.get(results, '0.main', '');
        const revisedContent = _.get(results, '1.main', '');
        if (originalContent && revisedContent) {
          const originalHtml = beautifyHTML(getCompareRevisionContent(originalContent));
          const revisedHtml = beautifyHTML(getCompareRevisionContent(revisedContent));
          const lines = diffLines(originalHtml, revisedHtml);
          setDiffResult(lines);
        }
      })
      .catch((error: any) => {
        console.error('コンテンツの取得中にエラーが発生しました:', error);
      });
  };
  const diffLineItems = useMemo((): DiffLineItem[] => {
    return diffResult.map((item, index) => {
      let mode = 'same';
      if (item.added) {
        mode = 'added';
      } else if (item.removed) {
        mode = 'removed';
      }
      return {
        id: 'diff-' + index,
        mode: mode,
        content: item.value,
        added: item.added,
        removed: item.removed,
      };
    });
  }, [diffResult]);

  // Revision item click
  const [currentRevisionItem, setCurrentRevisionItem] = useState<RevisionItem | null>(null);
  const handleRevisionItemClick = (revisionItem: RevisionItem) => {
    setCurrentRevisionItem(revisionItem);
    setSelectedRevisionIndexes(null);
    const index = revisionItem.index;
    const originalRevision = allRevisions[index];
    const revisedRevision = allRevisions[index + 1];
    makeDiff(revisedRevision, originalRevision);
  };
  const getRevisionItemClassNames = (item: RevisionItem) => {
    const targetIndex = item.index;
    if (
      selectedRevisionIndexes &&
      targetIndex >= selectedRevisionIndexes.startIndex &&
      targetIndex <= selectedRevisionIndexes.endIndex
    ) {
      // 範囲選択中の場合
      return 'revision-item-container selected';
    } else if (currentRevisionItem && currentRevisionItem.id === item.id) {
      // （ひとつだけ）選択中の場合
      return 'revision-item-container active';
    }
    return 'revision-item-container';
  };
  const init = () => {
    const targetRevisionItem = !currentRevisionItem ? revisionItems[0] : currentRevisionItem;
    if (allRevisions.length >= 2) {
      setCurrentRevisionItem(targetRevisionItem);
      const originalRevision = allRevisions[0];
      const revisedRevision = allRevisions[1];
      makeDiff(revisedRevision, originalRevision);
    }
  };
  useEffect(() => {
    init();
  }, []);

  // Revision mouse hover
  const [hoveredRevisionItem, setHoveredRevisionItem] = useState<RevisionItem | null>(null);
  const handleRevisionItemMouseEnter = (revisionItem: RevisionItem) => {
    setHoveredRevisionItem(revisionItem);
  };
  const handleRevisionItemMouseLeave = (revisionItem: RevisionItem) => {
    setHoveredRevisionItem(null);
  };
  const shouldShowOptionButton = (revisionItem: RevisionItem) => {
    return hoveredRevisionItem && hoveredRevisionItem.id === revisionItem.id;
  };

  // Revision select
  type RevisionSelectedData = {
    startIndex: number;
    endIndex: number;
  };
  const [selectedRevisionIndexes, setSelectedRevisionIndexes] = useState<RevisionSelectedData | null>(null);

  // Revision Rename
  const [showRenameWindow, setShowRenameWindow] = useState<boolean>(false);
  // internal_revision_id, edit_content_id で特定

  // Revision option
  const [showDropdownId, setShowDropdownId] = useState<string | null>(null);
  const dropdownMode = useRef<string | null>(null);
  const currentOptionItem = useRef<RevisionItem | null>(null);
  const shouldShowDropdown = (revisionItem: RevisionItem) => showDropdownId === revisionItem.id;
  window.addEventListener('click', (e: MouseEvent) => closeDropdown(e));
  const closeDropdown = (e: MouseEvent | React.MouseEvent) => {
    if (dropdownMode.current) {
      if (dropdownMode.current === 'compare') {
        const startIndex =
          currentOptionItem.current!.index > currentRevisionItem!.index
            ? currentRevisionItem!.index
            : currentOptionItem.current!.index;
        const endIndex =
          currentOptionItem.current!.index > currentRevisionItem!.index
            ? currentOptionItem.current!.index
            : currentRevisionItem!.index;
        setSelectedRevisionIndexes({
          startIndex,
          endIndex,
        });
        const startRevision = allRevisions[startIndex];
        const endRevision = allRevisions[endIndex];
        if (startRevision && endRevision) {
          makeDiff(endRevision, startRevision);
        }
      } else if (dropdownMode.current === 'name') {
        setShowRenameWindow(true);
      }
      e.preventDefault();
      e.stopPropagation();
    }
    setShowDropdownId(null);
    dropdownMode.current = null;
  };
  const handleRevisionOptionClick = (revisionItem: RevisionItem) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdownId(revisionItem.id);
  };
  const handleCompareOptionClick = (e: React.MouseEvent, item: RevisionItem) => {
    currentOptionItem.current = item;
    e.preventDefault();
    e.stopPropagation();
    dropdownMode.current = 'compare';
    closeDropdown(e);
  };
  const handleNameOptionClick = (e: React.MouseEvent, item: RevisionItem) => {
    currentOptionItem.current = item;
    e.preventDefault();
    e.stopPropagation();
    dropdownMode.current = 'name';
    closeDropdown(e);
  };
  /* eslint-enable */

  // Back to editing
  const handleBackToEditing = () => {
    switchEditor('editor');
    setDiffResult([]);
  };

  // Close rename window
  const handleClose = () => {
    setShowRenameWindow(false);
  };

  // 親側へ関数を公開
  useImperativeHandle(
    ref,
    () => ({
      init,
    }),
    [init]
  );

  return (
    <div id="revision-preview-diff-container">
      <div id="left-pane">
        <div id="left-pane-header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <button
                className="ck ck-button ck-revision-history-ui__back-to-editing ck-off ck-button_with-text"
                type="button"
                onClick={() => {
                  handleBackToEditing();
                }}
              >
                <svg
                  className="ck ck-icon ck-reset_all-excluded ck-icon_inherit-color ck-button__icon"
                  viewBox="0 0 20 20"
                >
                  <path d="m5.042 9.367 2.189 1.837a.75.75 0 0 1-.965 1.149l-3.788-3.18a.747.747 0 0 1-.21-.284.75.75 0 0 1 .17-.945L6.23 4.762a.75.75 0 1 1 .964 1.15L4.863 7.866h8.917A.75.75 0 0 1 14 7.9a4 4 0 1 1-1.477 7.718l.344-1.489a2.5 2.5 0 1 0 1.094-4.73l.008-.032H5.042z" />
                </svg>
                &nbsp;
                <span className="ck ck-button__label">{t('編集へ戻る')}</span>
              </button>
            </div>
            <div style={{ paddingRight: '5px' }}>
              <small>{t('変更点: {{count}}件', { count: diffResultCount })}</small>
            </div>
          </div>
        </div>
        <div id="left-pane-body">
          {diffLineItems.map((item) => (
            <div key={item.id}>
              {(item.mode === 'added' && (
                <div className="added">
                  <div className="diff-type-symbol">+</div>
                  <div className="diff-item-content">{item.content}</div>
                </div>
              )) ||
                (item.mode === 'removed' && (
                  <div className="removed">
                    <div className="diff-type-symbol">-</div>
                    <div className="diff-item-content">{item.content}</div>
                  </div>
                )) || <div className="same">{item.content}</div>}
            </div>
          ))}
        </div>
      </div>
      <div id="right-pane">
        <div id="right-pane-header">
          <svg viewBox="0 0 20 20">
            <g>
              <path d="M11 1a9 9 0 1 1-8.027 13.075l1.128-1.129A7.502 7.502 0 0 0 18.5 10a7.5 7.5 0 1 0-14.962.759l-.745-.746-.76.76A9 9 0 0 1 11 1z" />
              <path d="M.475 8.17a.75.75 0 0 1 .978.047l.075.082 1.284 1.643 1.681-1.284a.75.75 0 0 1 .978.057l.073.083a.75.75 0 0 1-.057.978l-.083.073-2.27 1.737a.75.75 0 0 1-.973-.052l-.074-.082-1.741-2.23a.75.75 0 0 1 .13-1.052z" />
              <path d="M11.5 5v4.999l3.196 3.196-1.06 1.06L10.1 10.72l-.1-.113V5z" />
            </g>
          </svg>
          {t('改訂履歴（DIFF）')}
        </div>
        <div id="right-pane-body">
          {Object.keys(groupedRevisions).map((date: string) => (
            <div key={date}>
              <div className="diff-period-container">
                <span className="diff-period-label">{date}</span>
              </div>
              <div>
                {groupedRevisions[date].map((item: RevisionItem) => (
                  <div
                    className={getRevisionItemClassNames(item)}
                    key={item.id}
                    onClick={() => handleRevisionItemClick(item)}
                    onMouseEnter={() => handleRevisionItemMouseEnter(item)}
                    onMouseLeave={() => handleRevisionItemMouseLeave(item)}
                  >
                    <div className="ck ck-reset ck-revision-history-sidebar__revision-wrapper">
                      <div className="ck ck-reset ck-revision-history-sidebar__revision">
                        {shouldShowOptionButton(item) && (
                          <button
                            type="button"
                            className="revision-option-button"
                            onClick={handleRevisionOptionClick(item)}
                          >
                            ︙
                          </button>
                        )}
                        {shouldShowDropdown(item) && (
                          <div className="revision-option-menu">
                            <ul>
                              <li onClick={(e) => handleCompareOptionClick(e, item)}>{t('選択したものと比較')}</li>
                              <li onClick={(e) => handleNameOptionClick(e, item)}>{t('この改訂版に名前をつける')}</li>
                            </ul>
                          </div>
                        )}
                        <div className="revision-item-name-container">{item.name}</div>
                        <div className="revision-item-date-container">{item.createdAt.format('YYYY/MM/DD HH:mm')}</div>
                        <small>
                          {item && item.users.length > 0 && (
                            <div className="revision-item-user-container">
                              <svg className="revision-item-user-icon" viewBox="0 0 11 10">
                                <path d="M5.5 5C8.538 5 11 7.015 11 9.5c0 .17-.011.336-.034.5H.034A3.732 3.732 0 0 1 0 9.5C0 7.015 2.462 5 5.5 5zm0-5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z" />
                              </svg>
                              <div>
                                {item.users.map((user: RevisionUser) => (
                                  <div key={user.name}>{user.name}</div>
                                ))}
                              </div>
                            </div>
                          )}
                        </small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {showRenameWindow && (
        <RevisionRenameWindow
          onClose={() => {
            setShowRenameWindow(false);
          }}
          headerContents={
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
              }}
            >
              {t('改訂版に名前をつける')}
            </div>
          }
        >
          {currentOptionItem.current && (
            <RevisionRenamePage
              editor={editor}
              sectionId={sectionId}
              revisionItem={currentOptionItem.current}
              onClose={handleClose}
            />
          )}
        </RevisionRenameWindow>
      )}
    </div>
  );
});

export default RevisionPreviewDiff;
